import RootStoreState from 'src/store/RootStoreState';
import { ActionContext } from 'vuex';
import { namespace } from 'vuex-class';
import { NormalizedCacheObject } from 'apollo-cache-inmemory';
import ApolloClient from 'apollo-client';
import {
  GQLCreateShiftAssignmentGroupMutation,
  GQLCreateShiftAssignmentGroupMutationVariables,
  GQLDeleteShiftAssignmentGroupMutation,
  GQLDeleteShiftAssignmentGroupMutationVariables,
  GQLShiftAssignmentGroupFragmentFragment,
} from 'codegen/gql-types';
import ApplicationLogger from 'services/logger/ApplicationLogger';
import {
  Action,
  ActionProvider,
  ById,
  createNormalizedStore,
} from 'store/normalized-store';
import { PayloadParameter, StoreActionResult } from 'utils/store';
import CreateShiftAssignmentGroupGql from './queries/CreateShiftAssignmentGroup.gql';
import DeleteShiftAssignmentGroupGql from './queries/DeleteShiftAssignmentGroup.gql';

export const shiftAssignmentGroupsNS = namespace('shiftAssignmentGroups');

export type StoreState = ById<ShiftAssignmentGroup>;

export type ShiftAssignmentGroup = GQLShiftAssignmentGroupFragmentFragment;

export type CreateShiftAssignmentGroupFunction = (
  payload: Omit<GQLCreateShiftAssignmentGroupMutationVariables, 'companyId'>,
) => Promise<StoreActionResult>;

export type DeleteShiftAssignmentGroupFunction = (
  payload: Omit<GQLDeleteShiftAssignmentGroupMutationVariables, 'companyId'>,
) => Promise<StoreActionResult>;

type StoreActionContext = ActionContext<StoreState, RootStoreState>;

const getShiftAssignmentGroupsStore = (
  graphqlClient: ApolloClient<NormalizedCacheObject>,
  logger: ApplicationLogger,
) => {
  const store = {
    namespaced: true,
    getters: {
      getByShiftId: (state: StoreState) => (shiftId: number) =>
        Object.values<ShiftAssignmentGroup>(state.byId).filter(
          (item) => item.shiftId === shiftId,
        ),
    },
  };

  const create: ActionProvider<
    GQLCreateShiftAssignmentGroupMutation,
    GQLCreateShiftAssignmentGroupMutationVariables
  > = (
    { rootState }: StoreActionContext,
    payload: PayloadParameter<CreateShiftAssignmentGroupFunction>,
  ) => {
    if (!rootState.auth.currentCompanyId) {
      throw new TypeError('currentCompanyId not provided');
    }

    return {
      query: CreateShiftAssignmentGroupGql,
      resultKey: 'createShiftAssignmentGroup',
      variables: {
        ...payload,
        companyId: rootState.auth.currentCompanyId,
      },
      useBatching: true,
    };
  };

  const remove: ActionProvider<
    GQLDeleteShiftAssignmentGroupMutation,
    GQLDeleteShiftAssignmentGroupMutationVariables
  > = (
    { rootState }: StoreActionContext,
    payload: PayloadParameter<DeleteShiftAssignmentGroupFunction>,
  ) => {
    if (!rootState.auth.currentCompanyId) {
      throw new TypeError('currentCompanyId not provided');
    }

    return {
      query: DeleteShiftAssignmentGroupGql,
      resultKey: 'deleteShiftAssignmentGroup',
      variables: {
        ...payload,
        companyId: rootState.auth.currentCompanyId,
      },
      useBatching: true,
    };
  };

  return createNormalizedStore<
    ShiftAssignmentGroup,
    StoreState,
    RootStoreState
  >({
    store,
    provide: {
      [Action.CREATE]: create,
      [Action.DELETE]: remove,
    },
    graphqlClient,
    logger,
  });
};

export default getShiftAssignmentGroupsStore;
